import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useModelesEnvoiStage } from './FenetreModeleCourriel';
import { stageAPI } from '../services/api';
import PhotoEtudiant from './PhotoEtudiant';
import { AvatarEnseignant } from './PhotoEnseignant';
import { teinteAvatar, initiales } from '../services/etudiantsAffichage';
import StageModale from './StageModale';
import StageModaleEnvoi from './StageModaleEnvoi';
import StageFenetrePromotion from './StageFenetrePromotion';

/**
 * AIDE À LA RÉPARTITION DES TUTEURS (lot 3.5b-2) — spec E140, E143, E150, E151 ;
 * maquette `#tab-tuteursrepartition` (page pleine, fenêtres Génération / Enregistrer / Revenir,
 * panneau des versions escamotable comme « Versions du planning »).
 *
 * ⚠️ RIEN N'EST ENREGISTRÉ AVANT « Enregistrer » : le tableau est un brouillon (`tableau`), comparé
 * à l'état enregistré (`base`) ; ce qui diffère est « Proposé » (contour vert pointillé). La
 * génération ne fait que remplir ce brouillon.
 * ⚠️ « Enregistrer » envoie, pour chaque carte changée, le tuteur que l'écran a VU (`avant`) : si
 * quelqu'un l'a changé ailleurs entre-temps, le serveur refuse TOUT et nomme les étudiants (RB3) ;
 * la page se recharge en gardant les autres propositions.
 * ⚠️ Lecture des cartes : la POPULATION à la bande (bleu FI, violet FA), le PROPOSÉ au contour
 * pointillé — une couleur ne porte jamais deux sens (recette maquette du 2026-10-08).
 */
const LECTURE_SEULE = 'Lecture seule : votre profil ne permet pas de modifier ce module.';
const nomEns = (e) => (e ? `${e.nom} ${e.prenom || ''}`.trim() : '');
const message = (e, defaut) => e.response?.data?.detail && typeof e.response.data.detail === 'string'
    ? e.response.data.detail : (e.response?.data?.error || defaut);
const coll = new Intl.Collator('fr');
const STATUTS = { mcf: 'MCF', prag: 'PRAG', past: 'PAST', ater: 'ATER', doctorant: 'Doctorant', vacataire: 'Vacataire', charge_cours: 'Chargé de cours' };
/** « 02/04 16:40 » à l'heure de Paris. */
const jourHeure = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '') : '');
const jour = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit' }) : '');

/** E140 : vert sous le souhaité, orange au souhaité, rouge au-delà. */
export function tonCharge(charge, souhaite) {
    if (charge > souhaite) return { couleur: '#dc2626', barre: '#dc2626', depasse: true };
    if (charge === souhaite && charge > 0) return { couleur: '#d97706', barre: '#f59e0b', depasse: false };
    return { couleur: '#16a34a', barre: '#16a34a', depasse: false };
}

function Carte({ c, propose, glissable, onDebut }) {
    const alt = c.type === 'alternant';
    const precedent = alt && !c.tuteur_courant && c.tuteur_precedent?.nom;
    const forme = String(c.formation || '').split(' · ');
    return (
        <div className={'stg-rep-card' + (alt ? ' popfa' : '') + (propose ? ' nouveau' : '') + (c.visite_faite ? ' verrou' : '')}
             draggable={glissable} onDragStart={glissable ? (e) => onDebut(e, c.cle) : undefined}
             title={c.visite_faite ? 'Visite faite : le tuteur ne change plus' : (glissable ? 'Glissez la carte vers un tuteur' : undefined)}>
            <PhotoEtudiant chemin={c.photo_url} className="stg-rep-av stg-rep-av-photo" alt=""
                secours={<div className="stg-rep-av" style={{ background: teinteAvatar(c.nom, c.prenom), color: '#fff' }}>{initiales(c.nom, c.prenom)}</div>} />
            <div className="stg-rep-txt">
                <div><b>{c.nom}</b> {c.prenom} <span className="stg-rep-f">{forme[0]}{forme.length > 1 && <span className="stg-rep-sem"> · {forme.slice(1).join(' · ')}</span>}</span>
                    {c.visite_faite && <> <i className="fa-solid fa-lock" title="Visite déjà faite" /></>}</div>
                <div className="stg-rep-ent"><i className="fa-solid fa-building" /> {c.entreprise_nom || <em>Entreprise pas encore connue</em>}</div>
                {(c.entreprise_cp || c.entreprise_ville) && (
                    <div className="stg-rep-adr"><i className="fa-solid fa-location-dot" /> {[c.entreprise_cp, c.entreprise_ville].filter(Boolean).join(' ')}</div>
                )}
                {precedent && <div className="stg-rep-prec" title="Tuteur pédagogique de l'année précédente (E151)"><i className="fa-solid fa-clock-rotate-left" /> l'an dernier : {c.tuteur_precedent.nom}</div>}
            </div>
        </div>
    );
}

function StageRepartition({ departementId, anneeId, modifiable, ouvrirGeneration = false, onRetour }) {
    const [donnees, setDonnees] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [tableau, setTableau] = useState({});            // cle → tuteur_id (brouillon)
    const [filtre, setFiltre] = useState([]);              // formations cochées (vide = toutes)
    const [compact, setCompact] = useState(true);
    const [parVille, setParVille] = useState(false);
    const [masquerComplets, setMasquerComplets] = useState(false);
    const [versionsOuvert, setVersionsOuvert] = useState(false);
    const [versions, setVersions] = useState(null);
    const [survol, setSurvol] = useState(null);
    const [fenetre, setFenetre] = useState(ouvrirGeneration ? 'generation' : null);
    const [enCours, setEnCours] = useState(false);
    const [info, setInfo] = useState(null);                // modale de résultat { titre, ton, corps }
    const derniereGeneration = useRef(null);
    const glisse = useRef(null);

    const lire = useCallback(async (garder = null) => {
        try {
            const r = await stageAPI.getRepartition(departementId, anneeId);
            setDonnees(r.data);
            setErreur(null);
            const base = Object.fromEntries(r.data.cartes.map((c) => [c.cle, c.tuteur_id || null]));
            // RB3 : après un conflit, on garde les propositions qui ne sont pas en cause.
            setTableau(garder ? { ...base, ...garder } : base);
            return r.data;
        } catch (e) { setErreur(message(e, 'Lecture de la répartition impossible')); return null; }
    }, [departementId, anneeId]);
    useEffect(() => { lire(); }, [lire]);

    const lireVersions = useCallback(async () => {
        try { setVersions((await stageAPI.getVersionsRepartition(departementId, anneeId)).data.versions); }
        catch (e) { setErreur(message(e, 'Lecture des versions impossible')); }
    }, [departementId, anneeId]);
    useEffect(() => { if (versionsOuvert) lireVersions(); }, [versionsOuvert, lireVersions]);

    // ── l'état dérivé du brouillon
    const cartes = useMemo(() => (donnees?.cartes || []).map((c) => ({ ...c, tuteur_courant: tableau[c.cle] ?? null })), [donnees, tableau]);
    const changes = useMemo(() => cartes.filter((c) => (c.tuteur_courant || null) !== (c.tuteur_id || null)), [cartes]);
    const enseignants = donnees?.enseignants || [];
    const parId = useMemo(() => new Map(enseignants.map((e) => [e.id, e])), [enseignants]);
    const charge = useMemo(() => {
        const m = new Map();
        for (const c of cartes) if (c.tuteur_courant) m.set(c.tuteur_courant, (m.get(c.tuteur_courant) || 0) + 1);
        return m;
    }, [cartes]);
    const dansFiltre = useCallback((c) => !filtre.length || filtre.includes(c.formation), [filtre]);

    // Les colonnes : les enseignants DISPONIBLES de l'année, plus ceux qui suivent encore quelqu'un
    // (non disponible ou hors de l'année : colonne GRISÉE, E150). Ordre stable : la charge enregistrée.
    const colonnes = useMemo(() => {
        const chargeBase = new Map();
        for (const c of donnees?.cartes || []) if (c.tuteur_id) chargeBase.set(c.tuteur_id, (chargeBase.get(c.tuteur_id) || 0) + 1);
        return enseignants
            .filter((e) => (e.disponible && e.actif !== false && !e.hors_annee) || (charge.get(e.id) || 0) > 0)
            .map((e) => ({ ...e, grisee: !(e.disponible && e.actif !== false && !e.hors_annee) }))
            .sort((a, b) => (chargeBase.get(b.id) || 0) - (chargeBase.get(a.id) || 0) || coll.compare(nomEns(a), nomEns(b)));
    }, [enseignants, charge, donnees]);

    const trier = useCallback((liste) => [...liste].sort((x, y) => (parVille
        ? coll.compare(x.entreprise_ville || '~', y.entreprise_ville || '~') || coll.compare(x.nom, y.nom)
        : coll.compare(x.formation || '', y.formation || '') || coll.compare(x.nom, y.nom))), [parVille]);

    const nonAffectes = cartes.filter((c) => !c.tuteur_courant);
    const pastilles = useMemo(() => {
        const m = new Map();
        for (const f of donnees?.formations || []) m.set(f.libelle, { ...f, sans: 0 });
        for (const c of cartes) if (!c.tuteur_courant && m.has(c.formation)) m.get(c.formation).sans += 1;
        return [...m.values()];
    }, [donnees, cartes]);

    // ── glisser-déposer
    const debut = (e, cle) => { glisse.current = cle; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', cle); };
    const permis = (col) => modifiable && !enCours && (col === null || !col.grisee);
    const survoler = (e, col) => { if (!glisse.current || !permis(col)) return; e.preventDefault(); setSurvol(col ? col.id : 'libre'); };
    const deposer = (e, col) => {
        e.preventDefault(); setSurvol(null);
        const cle = glisse.current || e.dataTransfer.getData('text/plain'); glisse.current = null;
        if (!cle || !permis(col)) return;
        const c = cartes.find((x) => x.cle === cle);
        if (!c || c.visite_faite) return;
        setTableau((t) => ({ ...t, [cle]: col ? col.id : null }));
    };

    // ── quitter sans enregistrer
    useEffect(() => {
        if (!changes.length) return undefined;
        const garde = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', garde);
        return () => window.removeEventListener('beforeunload', garde);
    }, [changes.length]);
    const retour = () => (changes.length ? setFenetre('quitter') : onRetour());

    // ── enregistrer
    const enregistrer = async (commentaire) => {
        setEnCours(true);
        try {
            const r = await stageAPI.enregistrerRepartition(departementId, anneeId, {
                commentaire, changements: changes.map((c) => ({ cle: c.cle, avant: c.tuteur_id || null, apres: c.tuteur_courant || null }))
            });
            setFenetre(null);
            await lire();
            setVersionsOuvert(true); await lireVersions();
            // Lot 3.5b-3 (maquette #mailTuteursOverlay) : proposer de prévenir les tuteurs qui gagnent des étudiants.
            if (r.data.tuteurs_concernes.length) setFenetre({ type: 'prevenir', resultat: r.data });
            else {
                setInfo({ titre: 'Répartition enregistrée', ton: 'succes', corps: (
                    <p>{r.data.changements.length} changement{r.data.changements.length > 1 ? 's' : ''} enregistré{r.data.changements.length > 1 ? 's' : ''} — c'est une nouvelle <b>version</b> (panneau de droite). Aucun tuteur n'a de nouvel étudiant : personne à prévenir.</p>
                ) });
            }
        } catch (e) {
            const detail = Array.isArray(e.response?.data?.detail) ? e.response.data.detail : null;
            if (e.response?.status === 409 && detail) {
                const enCause = new Set(detail.map((d) => d.cle));
                const garder = Object.fromEntries(changes.filter((c) => !enCause.has(c.cle)).map((c) => [c.cle, c.tuteur_courant || null]));
                setFenetre(null);
                setInfo({ titre: "Rien n'a été enregistré", ton: 'danger', corps: (
                    <>
                        <p>{e.response.data.error}</p>
                        <ul className="stg-rep-liste">
                            {detail.map((d) => <li key={d.cle}><b>{d.etudiant || d.cle}</b> {d.raison}{d.tuteur_actuel !== undefined ? ` — tuteur actuel : ${d.tuteur_actuel || 'aucun'}` : ''}</li>)}
                        </ul>
                        <p>La page est relue : ces étudiants affichent leur tuteur réel, <b>vos autres propositions sont gardées</b>. Vérifiez puis enregistrez de nouveau.</p>
                    </>
                ) });
                await lire(garder);
            } else {
                setInfo({ titre: "L'enregistrement n'a pas abouti", ton: 'danger', corps: <p>{message(e, 'Erreur inattendue.')}</p> });
            }
        } finally { setEnCours(false); }
    };

    if (!donnees) {
        return erreur ? <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>
                      : <div className="stg-attente"><i className="fa-solid fa-circle-notch stg-tourne" /><div>Lecture de la répartition…</div></div>;
    }

    const colonnesVues = colonnes.filter((col) => !(masquerComplets && (charge.get(col.id) || 0) >= col.souhaite));
    const nb = changes.length;

    return (
        <div className="stg-rep">
            <div className="stg-filtres" style={{ flexWrap: 'wrap', gap: 8 }}>
                <button type="button" className="btn btn-outline btn-sm" onClick={retour}><i className="fa-solid fa-arrow-left" /> Retour aux tuteurs</button>
                <strong style={{ fontSize: 14, marginLeft: 6 }}>Aide à la répartition{donnees.annee?.libelle ? ` — ${donnees.annee.libelle}` : ''}</strong>
                <label className="stg-rep-case" title="Trie les cartes de chaque colonne par ville de l'entreprise">
                    <input type="checkbox" checked={parVille} onChange={(e) => setParVille(e.target.checked)} /> Regrouper par ville
                </label>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                    {/* Même compte que la colonne « Non affectés » : celui du FILTRE ; le total en infobulle. */}
                    {(() => {
                        const n = nonAffectes.filter(dansFiltre).length;
                        const texte = `${n} non affecté${n > 1 ? 's' : ''}${filtre.length ? ' dans le filtre' : ''}`;
                        return (
                            <span className="badge stg-rep-compteur" title={filtre.length ? `${nonAffectes.length} non affecté${nonAffectes.length > 1 ? 's' : ''} en tout` : undefined}>
                                {derniereGeneration.current && nb ? `Proposition — ${texte}` : texte}
                            </span>
                        );
                    })()}
                    <button type="button" className="btn btn-outline btn-sm" disabled={!modifiable || enCours} title={modifiable ? undefined : LECTURE_SEULE}
                            onClick={() => setFenetre('generation')}><i className="fa-solid fa-wand-magic-sparkles" /> Génération automatique</button>
                    <button type="button" className="btn btn-green btn-sm" disabled={!modifiable || enCours || !nb}
                            title={!modifiable ? LECTURE_SEULE : (nb ? `${nb} changement${nb > 1 ? 's' : ''} à enregistrer` : 'Aucun changement à enregistrer')}
                            onClick={() => setFenetre('enregistrer')}><i className="fa-solid fa-floppy-disk" /> Enregistrer{nb ? ` (${nb})` : ''}</button>
                    <button type="button" className={'btn btn-outline btn-sm' + (versionsOuvert ? ' stg-rep-actif' : '')} title="Versions de la répartition"
                            aria-pressed={versionsOuvert} onClick={() => setVersionsOuvert((v) => !v)}><i className="fa-solid fa-clock-rotate-left" /></button>
                </div>
            </div>

            <div className="stg-rep-info">
                <i className="fa-solid fa-circle-info" /> Glissez les cartes d'une colonne à l'autre, ou lancez une <b>génération automatique</b> puis ajustez.{' '}
                <b>Rien n'est enregistré</b> avant « Enregistrer » — chaque enregistrement devient une <b>version</b> (bouton <i className="fa-solid fa-clock-rotate-left" />).{' '}
                <span className="stg-rep-leg fi">Stagiaire FI</span> <span className="stg-rep-leg popfa">Alternant FA</span> (pas de fiche de visite dans l'application) ·{' '}
                <span className="stg-rep-leg prop">Proposé</span> = placement pas encore enregistré · <i className="fa-solid fa-lock" /> = visite faite (ne bouge plus).
            </div>
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

            <div className="stg-rep-filtres">
                <span className="stg-rep-filtres-lib">Formations :</span>
                {pastilles.map((f) => (
                    <button type="button" key={f.libelle} className={'stg-rep-chip' + (f.type === 'alternant' ? ' alt' : '') + (filtre.includes(f.libelle) ? ' actif' : '')}
                            aria-pressed={filtre.includes(f.libelle)}
                            onClick={() => setFiltre((x) => (x.includes(f.libelle) ? x.filter((y) => y !== f.libelle) : [...x, f.libelle]))}>
                        {f.libelle} <b>{f.sans}</b>
                    </button>
                ))}
                <span className="stg-rep-note">(nombre = sans tuteur · rien de coché = tout)</span>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 14 }}>
                    <label className="stg-rep-case"><input type="checkbox" checked={compact} onChange={(e) => setCompact(e.target.checked)} /> Cartes compactes</label>
                    <label className="stg-rep-case"><input type="checkbox" checked={masquerComplets} onChange={(e) => setMasquerComplets(e.target.checked)} /> Masquer les tuteurs complets</label>
                </span>
            </div>

            <div className="stg-rep-corps">
                <div className={'stg-rep-board' + (compact ? ' compact' : '')}>
                    {/* Non affectés — collée à gauche au défilement */}
                    <div className="stg-rep-col stg-rep-col-libre">
                        <div className="stg-rep-col-h">
                            <div><b>Non affectés</b>
                                {nonAffectes.length > 0 && derniereGeneration.current?.plusDePlace && <div className="stg-rep-col-sous">plus de place : chaque tuteur disponible a atteint son nombre souhaité</div>}
                            </div>
                            <span className="stg-rep-quota" style={{ color: '#d97706' }}>{nonAffectes.filter(dansFiltre).length}</span>
                        </div>
                        <div className={'stg-rep-col-b' + (survol === 'libre' ? ' survol' : '')} onDragOver={(e) => survoler(e, null)} onDragLeave={() => setSurvol(null)} onDrop={(e) => deposer(e, null)}>
                            {trier(nonAffectes.filter(dansFiltre)).map((c) => (
                                <Carte key={c.cle} c={c} propose={c.tuteur_id && !c.tuteur_courant} glissable={modifiable && !c.visite_faite} onDebut={debut} />
                            ))}
                        </div>
                    </div>
                    {colonnesVues.map((col) => {
                        const ses = cartes.filter((c) => c.tuteur_courant === col.id);
                        const n = ses.length;
                        const ton = tonCharge(n, col.souhaite);
                        const vues = ses.filter(dansFiltre);
                        return (
                            <div key={col.id} className={'stg-rep-col' + (col.grisee ? ' grisee' : '')}
                                 title={col.grisee ? (col.hors_annee ? "N'est plus dans les enseignants de l'année : déplacez ses étudiants" : 'Non disponible : on ne lui dépose plus de carte (E150)') : undefined}>
                                <div className="stg-rep-col-h">
                                    <AvatarEnseignant e={col} className="stg-tut-av" />
                                    <div>
                                        <b>{nomEns(col)}</b>
                                        <div className="stg-rep-col-sous">{col.grisee ? (col.hors_annee ? "plus dans l'année" : 'non disponible') : (STATUTS[col.statut] || 'Enseignant')}</div>
                                        {filtre.length > 0 && <div className="stg-rep-col-filtre">dont {vues.length} dans le filtre</div>}
                                    </div>
                                    <span className="stg-rep-quota" style={{ color: ton.couleur }}>{n} / {col.souhaite}{ton.depasse ? ' ⚠️' : ''}</span>
                                </div>
                                <div className={'stg-rep-col-b' + (survol === col.id ? ' survol' : '')} onDragOver={(e) => survoler(e, col)} onDragLeave={() => setSurvol(null)} onDrop={(e) => deposer(e, col)}>
                                    {trier(vues).map((c) => (
                                        <Carte key={c.cle} c={c} propose={(c.tuteur_courant || null) !== (c.tuteur_id || null)} glissable={modifiable && !c.visite_faite} onDebut={debut} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    {!colonnesVues.length && (
                        <div className="stg-section-avenir" style={{ alignSelf: 'center' }}>
                            <i className="fa-solid fa-circle-info" /> {colonnes.length ? 'Tous les tuteurs sont complets (case « Masquer les tuteurs complets »).' : "Aucun enseignant disponible cette année : ajoutez-les dans le module Enseignants, ou cochez « disponible » dans la génération automatique."}
                        </div>
                    )}
                </div>

                <PanneauVersions ouvert={versionsOuvert} versions={versions} nb={nb} modifiable={modifiable && !enCours}
                                 onFermer={() => setVersionsOuvert(false)}
                                 onRevenir={(v) => setFenetre({ type: 'revenir', version: v })}
                                 onSupprimer={(v) => setFenetre({ type: 'supprimer', version: v })} />
            </div>

            {fenetre === 'generation' && (
                <FenetreGeneration departementId={departementId} anneeId={anneeId} donnees={donnees} cartes={cartes} charge={charge}
                                   filtre={filtre} enCours={enCours} setEnCours={setEnCours}
                                   onFermer={() => setFenetre(null)}
                                   onProposition={(r, souhaits) => {
                                       derniereGeneration.current = { plusDePlace: r.non_affectes > 0 && !r.aucun_tuteur_disponible };
                                       setDonnees((d) => ({ ...d, souhait_defaut: souhaits.defaut,
                                           enseignants: d.enseignants.map((e) => { const s = souhaits.enseignants.find((x) => x.id === e.id); return s ? { ...e, souhaite: s.souhaite, disponible: s.disponible, souhait_origine: s.origine } : e; }) }));
                                       setTableau((t) => ({ ...t, ...Object.fromEntries(r.placements.map((p) => [p.cle, p.tuteur_id])) }));
                                       setFenetre(null);
                                       if (r.aucun_tuteur_disponible) setInfo({ titre: 'Aucun tuteur disponible', ton: 'danger', corps: <p>Aucun enseignant n'est coché « disponible » avec un nombre de suivis souhaités supérieur à 0 : rien n'a pu être proposé.</p> });
                                   }} />
            )}
            {fenetre === 'enregistrer' && (
                <FenetreEnregistrer changes={changes} parId={parId} enCours={enCours} onFermer={() => setFenetre(null)} onEnregistrer={enregistrer} />
            )}
            {fenetre?.type === 'revenir' && (
                <FenetreRevenir departementId={departementId} anneeId={anneeId} version={fenetre.version} brouillon={nb} enCours={enCours} setEnCours={setEnCours}
                                onFermer={() => setFenetre(null)}
                                onFait={async (r) => {
                                    setFenetre(null); await lire(); await lireVersions();
                                    setInfo({ titre: 'Retour effectué', ton: 'succes', corps: r.inchange
                                        ? <p>La répartition était déjà celle de cette version : rien n'a changé.</p>
                                        : <p>{r.changements.length} étudiant{r.changements.length > 1 ? 's ont' : ' a'} retrouvé le tuteur de la version du {r.ecart.version.jour}. Ce retour est lui-même une nouvelle version.</p> });
                                }} />
            )}
            {fenetre?.type === 'supprimer' && (
                <StageModale titre="Supprimer cette version du fil ?" icone="fa-trash" ton="danger" enCours={enCours} libelleValider="Supprimer du fil"
                             onFermer={() => setFenetre(null)}
                             onValider={async () => {
                                 setEnCours(true);
                                 try { await stageAPI.supprimerVersionRepartition(departementId, anneeId, fenetre.version.id); setFenetre(null); await lireVersions(); }
                                 catch (e) { setFenetre(null); setInfo({ titre: 'Suppression refusée', ton: 'danger', corps: <p>{message(e, 'Erreur inattendue.')}</p> }); }
                                 finally { setEnCours(false); }
                             }}>
                    <p>La version du <b>{jourHeure(fenetre.version.created_at)}</b>{fenetre.version.libelle ? <> « {fenetre.version.libelle} »</> : ''} quitte le fil : on ne pourra plus y revenir.</p>
                    <p className="stg-alt-sous">La répartition actuelle n'est pas touchée.</p>
                </StageModale>
            )}
            {fenetre?.type === 'prevenir' && (
                <FenetrePrevenir resultat={fenetre.resultat}
                                 onFermer={() => setFenetre(null)}
                                 onPreparer={(choix) => setFenetre({ type: 'groupe', choix })} />
            )}
            {fenetre?.type === 'groupe' && (
                <EnvoisGroupes departementId={departementId} anneeId={anneeId} choix={fenetre.choix}
                               onSerie={(demandes) => setFenetre(demandes.length ? { type: 'serie', demandes } : null)}
                               onFin={() => { setFenetre(null); lire(); }} />
            )}
            {fenetre?.type === 'serie' && (
                <SerieEnvoi departementId={departementId} anneeId={anneeId} demandes={fenetre.demandes}
                            onFin={(bilan) => {
                                setFenetre(null); lire();
                                setInfo({ titre: 'Messages aux tuteurs', ton: 'succes', corps: <BilanSerie bilan={bilan} /> });
                            }} />
            )}
            {fenetre === 'quitter' && (
                <StageModale titre="Quitter sans enregistrer ?" icone="fa-triangle-exclamation" ton="danger" libelleValider="Quitter sans enregistrer" libelleAnnuler="Rester sur la page"
                             onFermer={() => setFenetre(null)} onValider={() => { setFenetre(null); onRetour(); }}>
                    <p><b>{nb} placement{nb > 1 ? 's' : ''}</b> {nb > 1 ? 'ne sont pas enregistrés' : "n'est pas enregistré"} : ils seront perdus.</p>
                </StageModale>
            )}
            {info && <StageModale titre={info.titre} icone={info.ton === 'succes' ? 'fa-circle-check' : 'fa-circle-exclamation'} ton={info.ton} onFermer={() => setInfo(null)}>{info.corps}</StageModale>}
        </div>
    );
}

/** `d:12` → stagiaire (dossier 12) ; `a:34` → alternant (étudiant 34) : la cible des courriels des tuteurs. */
const cibleDe = (cle) => (cle.startsWith('d:') ? { type: 'stagiaire', dossier_id: Number(cle.slice(2)) } : { type: 'alternant', etudiant_id: Number(cle.slice(2)) });

/**
 * « Prévenir les tuteurs ? » (maquette #mailTuteursOverlay) — après « Enregistrer ».
 * Un message par tuteur coché (modèle « Information tuteur »), qui liste SES NOUVEAUX étudiants ;
 * « Prévenir aussi leurs maîtres » = un message par étudiant (« Information du suivi »), jamais pour
 * un apprenti suivi par le CFA (E149 ; L3b-2 : sa formation n'est cochée dans aucun processus de suivi).
 */
function FenetrePrevenir({ resultat, onFermer, onPreparer }) {
    // Par ordre alphabétique : la série suit le même ordre que la liste.
    const tuteurs = [...resultat.tuteurs_concernes].sort((x, y) => coll.compare(x.tuteur || '', y.tuteur || ''));
    const [coches, setCoches] = useState(() => new Set(tuteurs.map((t) => t.tuteur_id)));
    const [maitres, setMaitres] = useState(false);
    const sansMaitre = (n) => n.type === 'alternant' && n.suivi_cfa === true;
    const avecMaitre = tuteurs.filter((t) => coches.has(t.tuteur_id)).flatMap((t) => t.nouveaux).filter((n) => !sansMaitre(n));
    const exclus = tuteurs.flatMap((t) => t.nouveaux).filter(sansMaitre).length;
    const demandesTuteurs = tuteurs.filter((t) => coches.has(t.tuteur_id)).map((t) => ({
        libelle: `Information tuteur à ${t.tuteur}`,
        demande: { genre: 'tuteur', tuteurId: t.tuteur_id, cibles: t.nouveaux.map((n) => cibleDe(n.cle)) }
    }));
    const demandesMaitres = maitres ? avecMaitre.map((n) => ({
        libelle: `Information du suivi au maître de ${n.etudiant}`,
        demande: { genre: 'maitre', cibles: [cibleDe(n.cle)] }
    })) : [];
    const demandes = [...demandesTuteurs, ...demandesMaitres];
    const basculer = (id) => setCoches((c) => { const s = new Set(c); if (s.has(id)) s.delete(id); else s.add(id); return s; });
    return (
        <StageModale titre="Prévenir les tuteurs ?" icone="fa-envelope" libelleAnnuler="Plus tard" onFermer={onFermer}
                     libelleValider={`Préparer ${demandes.length} message${demandes.length > 1 ? 's' : ''}`}
                     onValider={demandes.length ? () => onPreparer({ tuteurs: demandesTuteurs, maitres: demandesMaitres }) : undefined}>
            <div style={{ marginBottom: 8 }}><b>Un seul message par tuteur</b>, qui liste ses nouveaux étudiants avec, pour chacun, le lien de sa fiche de visite (modèle « Information tuteur ») :</div>
            <div className="stg-rep-prevenir">
                {tuteurs.map((t) => (
                    <label key={t.tuteur_id}>
                        <input type="checkbox" checked={coches.has(t.tuteur_id)} onChange={() => basculer(t.tuteur_id)} />{' '}
                        <b>{t.tuteur}</b> — {t.nouveaux.length} {t.nouveaux.length > 1 ? 'nouveaux étudiants' : 'nouvel étudiant'} :{' '}
                        {t.nouveaux.map((n, i) => (
                            <span key={n.cle}>{i ? ', ' : ''}{n.etudiant}{n.type === 'alternant' && <> <span className="stg-form alt">FA</span></>}</span>
                        ))}
                    </label>
                ))}
            </div>
            {avecMaitre.length > 0 || maitres ? (
                <label style={{ display: 'block', marginTop: 8 }}>
                    <input type="checkbox" checked={maitres} onChange={(e) => setMaitres(e.target.checked)} />{' '}
                    Prévenir aussi leurs maîtres de stage (modèle « Information du suivi », un message par étudiant{avecMaitre.length ? ` : ${avecMaitre.length}` : ''})
                </label>
            ) : null}
            {exclus > 0 && <div className="stg-alt-sous" style={{ marginTop: 4 }}><i className="fa-solid fa-handshake" /> Les apprentis sont suivis par le CFA : aucun message à leur maître d'apprentissage.</div>}
            <div className="stg-alt-sous" style={{ marginTop: 8 }}>La fenêtre d'envoi groupé s'ouvre ensuite — <b>la même que pour la promotion</b> : une ligne par message, un texte pour tous, « Aperçu, message par message », essai, « Envoyer ». Les tuteurs d'abord, puis les maîtres si la case est cochée. Rien ne part sans vous.</div>
        </StageModale>
    );
}

/**
 * La série : chaque demande est PRÉPARÉE par le serveur au moment de l'afficher (brouillon façon
 * Outlook, ou `mailto` si le département n'envoie pas par le serveur), puis la fenêtre habituelle
 * s'ouvre avec le bandeau « Message i sur N ». Une demande que le serveur refuse (tuteur sans
 * adresse…) est dite, puis on passe.
 */
function SerieEnvoi({ departementId, anneeId, demandes, onFin }) {
    const [i, setI] = useState(0);
    const [envoi, setEnvoi] = useState(null);
    const [refus, setRefus] = useState(null);
    const bilan = useRef([]);
    const fini = useRef(false);
    const terminer = useCallback(() => {
        if (fini.current) return; fini.current = true;
        const vus = new Set(bilan.current.map((b) => b.i));
        onFin([...bilan.current, ...demandes.map((d, k) => ({ i: k, libelle: d.libelle, etat: 'non_traite' })).filter((b) => !vus.has(b.i))]);
    }, [demandes, onFin]);
    const noter = (etat, detail = null) => { bilan.current = [...bilan.current, { i, libelle: demandes[i].libelle, etat, detail }]; };
    const suivant = (etat, detail) => {
        noter(etat, detail); setEnvoi(null); setRefus(null);
        if (i + 1 >= demandes.length) terminer(); else setI(i + 1);
    };
    useEffect(() => {
        let vivant = true;
        stageAPI.preparerEnvoiTuteurs(departementId, anneeId, demandes[i].demande)
            .then((r) => vivant && setEnvoi(r.data))
            .catch((e) => {
                if (!vivant) return;
                // Le refus ET sa consigne (« Le maître n'a pas d'adresse » + « Elle vient de la fiche de liaison… »).
                const d = e.response?.data || {};
                setRefus([d.error || "Le message n'a pas pu être préparé", typeof d.detail === 'string' ? d.detail : null].filter(Boolean).join(' — '));
            });
        return () => { vivant = false; };
    }, [departementId, anneeId, demandes, i]);
    const serie = { position: i + 1, total: demandes.length, libelle: demandes[i].libelle,
                    onSuivant: (etat) => suivant(etat), onArreter: () => { noter('arrete'); terminer(); } };
    if (refus) {
        return (
            <StageModale titre={`Message ${i + 1} sur ${demandes.length}`} icone="fa-circle-exclamation" ton="danger"
                         libelleValider={i + 1 < demandes.length ? 'Passer au suivant' : 'Terminer la série'} onValider={() => suivant('refuse', refus)}
                         libelleAnnuler="Arrêter la série" onFermer={() => { noter('refuse', refus); terminer(); }}>
                <p><b>{demandes[i].libelle}</b> : {refus}</p>
            </StageModale>
        );
    }
    if (!envoi) return <StageModale titre={`Message ${i + 1} sur ${demandes.length}`} icone="fa-circle-notch" enCours onFermer={() => {}}><p>Préparation du message…</p></StageModale>;
    return <StageModaleEnvoi key={i} envoi={envoi} departementId={departementId} serie={serie} onFermer={serie.onArreter} onTermine={() => {}}
                             rePreparer={() => stageAPI.preparerEnvoiTuteurs(departementId, anneeId, demandes[i].demande).then((r) => r.data)} />;
}

/**
 * Lot 3.5b-4 (E154) — les envois GROUPÉS, dans la fenêtre de la promotion et de la certification :
 * « Information tuteur » (une ligne par tuteur), PUIS « Information du suivi » (une ligne par maître) si la
 * case est cochée. Sans serveur d'envoi (`mode: 'mailto'`), on retombe sur la SÉRIE (E152) : un `mailto:`
 * n'ouvre qu'un message à la fois.
 */
function EnvoisGroupes({ departementId, anneeId, choix, onSerie, onFin }) {
    const etapes = useMemo(() => [
        choix.tuteurs.length && { genre: 'tuteur', corps: { genre: 'tuteur', groupes: choix.tuteurs.map((d) => ({ tuteurId: d.demande.tuteurId, cibles: d.demande.cibles })) }, serie: choix.tuteurs },
        choix.maitres.length && { genre: 'maitre', corps: { genre: 'maitre', cibles: choix.maitres.map((d) => d.demande.cibles[0]) }, serie: choix.maitres }
    ].filter(Boolean), [choix]);
    const [i, setI] = useState(0);
    const [brouillon, setBrouillon] = useState(null);
    const [erreur, setErreur] = useState(null);
    // ⚠️ Les rappels du parent passent par une RÉFÉRENCE : dans les dépendances, ils relanceraient la préparation à chaque rendu.
    const rappels = useRef({ onSerie, onFin });
    rappels.current = { onSerie, onFin };
    const etape = etapes[i];
    useEffect(() => {
        let vivant = true;
        setBrouillon(null); setErreur(null);
        if (!etape) { rappels.current.onFin(); return undefined; }
        stageAPI.preparerGroupeTuteurs(departementId, anneeId, etape.corps)
            .then((r) => {
                if (!vivant) return;
                // Sans serveur : tout ce qui reste part en série (mailto, un message à la fois).
                if (r.data.mode === 'mailto') rappels.current.onSerie(etapes.slice(i).flatMap((x) => x.serie));
                else setBrouillon(r.data);
            })
            .catch((e) => vivant && setErreur([e.response?.data?.error, typeof e.response?.data?.detail === 'string' ? e.response.data.detail : null].filter(Boolean).join(' — ') || "L'envoi n'a pas pu être préparé"));
        return () => { vivant = false; };
    }, [departementId, anneeId, etapes, etape, i]);
    const api = useMemo(() => etape && ({
        lancer: (dep, corps) => stageAPI.lancerGroupeTuteurs(dep, anneeId, { ...corps, ...etape.corps }),
        etat: (dep, id) => stageAPI.etatGroupeTuteurs(dep, anneeId, id)
    }), [anneeId, etape]);
    const suivante = () => setI((k) => k + 1);
    // Lot MC : « Modèles de courriel… » — refaire le brouillon de l'étape en cours
    const { onModeles, fenetre } = useModelesEnvoiStage({ departementId, typeMail: brouillon?.type_mail,
        rePreparer: () => stageAPI.preparerGroupeTuteurs(departementId, anneeId, etape.corps).then((r) => r.data) });
    const titre = etape?.genre === 'maitre' ? 'Information du suivi' : 'Information tuteur';
    if (!etape) return null;
    if (erreur) return <StageModale titre={titre} icone="fa-circle-exclamation" ton="danger" libelleAnnuler={i + 1 < etapes.length ? 'Continuer' : 'Fermer'} onFermer={suivante}><p>{erreur}</p></StageModale>;
    if (!brouillon) return <StageModale titre={titre} icone="fa-circle-notch" enCours onFermer={() => {}}><p>Préparation des messages…</p></StageModale>;
    return <><StageFenetrePromotion key={i} brouillon={brouillon} departementId={departementId} api={api} onFermer={suivante} onTermine={() => {}} onModeles={onModeles} />{fenetre}</>;
}

const ETATS_SERIE = { envoye: 'envoyé', prepare: 'préparé dans la messagerie', passe: 'passé', echec: "échec de l'envoi", refuse: 'refusé', arrete: 'série arrêtée ici', non_traite: 'non traité' };
function BilanSerie({ bilan }) {
    const partis = bilan.filter((b) => b.etat === 'envoye' || b.etat === 'prepare').length;
    return (
        <>
            <p><b>{partis}</b> message{partis > 1 ? 's' : ''} sur {bilan.length} {partis > 1 ? 'sont partis' : 'est parti'} (ou préparé{partis > 1 ? 's' : ''} dans la messagerie). Chaque étudiant concerné affiche « prévenu le … » dans le volet de son tuteur.</p>
            <ul className="stg-rep-liste">
                {bilan.map((b) => <li key={b.i}>{b.libelle} — <b>{ETATS_SERIE[b.etat] || b.etat}</b>{b.detail ? ` (${b.detail})` : ''}</li>)}
            </ul>
        </>
    );
}

/** Le panneau des versions — même mécanique que « Versions du planning » : fermé par défaut, × pour fermer. */
function PanneauVersions({ ouvert, versions, nb, modifiable, onFermer, onRevenir, onSupprimer }) {
    return (
        <div className={'stg-rep-side' + (ouvert ? ' open' : '')} aria-hidden={!ouvert}>
            <div className="stg-rep-side-h"><i className="fa-solid fa-clock-rotate-left" /> Versions de la répartition
                <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 'auto', padding: '2px 7px' }} onClick={onFermer} title="Fermer"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="stg-rep-side-b">
                {nb > 0 && (
                    <div className="stg-rv"><div className="stg-rv-h"><b>Non enregistrée</b> <span className="badge stg-rv-encours">en cours</span></div>
                        <div className="stg-rv-m">{nb} placement{nb > 1 ? 's' : ''} proposé{nb > 1 ? 's' : ''}</div></div>
                )}
                {!versions && <div className="stg-rv stg-rv-w"><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture…</div>}
                {versions && !versions.length && <div className="stg-rv stg-rv-w">Aucune version : la première est créée au premier « Enregistrer ».</div>}
                {(versions || []).map((v) => (
                    <div key={v.id} className={'stg-rv' + (v.actuelle ? ' cur' : '')}>
                        <div className="stg-rv-h"><b>{jourHeure(v.created_at)}</b>
                            {v.actuelle ? <span className="badge badge-ok" style={{ fontSize: 9 }}>actuelle</span>
                                : modifiable && <button type="button" className="stg-rv-del" title="Supprimer du fil" onClick={() => onSupprimer(v)}><i className="fa-solid fa-trash" /></button>}
                        </div>
                        <div className="stg-rv-m">{v.automatique ? <i>{v.libelle}</i> : (v.libelle ? `« ${v.libelle} »` : <span className="stg-rv-w">sans commentaire</span>)}</div>
                        {v.nb_changements > 0 && (
                            <div className="stg-rep-v-chg">
                                {v.changements.map((c, i) => <div key={i}>{c.etudiant} : {c.avant || '—'} → {c.apres || <i>sans tuteur</i>}</div>)}
                                {v.nb_changements > v.changements.length && <div>… et {v.nb_changements - v.changements.length} autre{v.nb_changements - v.changements.length > 1 ? 's' : ''}</div>}
                            </div>
                        )}
                        <div className="stg-rv-w"><i className="fa-solid fa-user" /> {v.auteur || 'auteur inconnu'}
                            {!v.actuelle && modifiable && <> · <button type="button" className="stg-rv-restore" onClick={() => onRevenir(v)}>↩ revenir à cette version</button></>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Fenêtre « Génération automatique » : qui répartir, combien de suivis par tuteur (gardés pour l'année). */
function FenetreGeneration({ departementId, anneeId, donnees, cartes, charge, filtre, enCours, setEnCours, onFermer, onProposition }) {
    const formations = donnees.formations || [];
    const [coches, setCoches] = useState(() => (filtre.length ? filtre : formations.map((f) => f.libelle)));
    const [portee, setPortee] = useState('sans_tuteur');
    const [defaut, setDefaut] = useState(donnees.souhait_defaut ?? 3);
    const tuteurs = (donnees.enseignants || []).filter((e) => e.actif !== false && !e.hors_annee).sort((a, b) => coll.compare(nomEns(a), nomEns(b)));
    const [reglages, setReglages] = useState(() => Object.fromEntries(tuteurs.map((e) => [e.id, { souhaite: e.souhaite, disponible: e.disponible }])));
    const [erreur, setErreur] = useState(null);
    const sansTuteur = (lib) => cartes.filter((c) => c.formation === lib && !c.tuteur_courant).length;
    const poser = (id, champ, v) => setReglages((r) => ({ ...r, [id]: { ...r[id], [champ]: v } }));
    const borne = (v) => Number.isInteger(Number(v)) && Number(v) >= 0 && Number(v) <= 30 && String(v) !== '';

    const lancer = async () => {
        if (!borne(defaut) || tuteurs.some((e) => !borne(reglages[e.id].souhaite))) { setErreur('Les nombres de suivis sont des entiers entre 0 et 30.'); return; }
        if (!coches.length) { setErreur('Cochez au moins une formation à répartir.'); return; }
        setEnCours(true); setErreur(null);
        try {
            const s = await stageAPI.enregistrerSouhaits(departementId, anneeId, {
                defaut: Number(defaut),
                enseignants: tuteurs.map((e) => ({ id: e.id, souhaite: Number(reglages[e.id].souhaite), disponible: !!reglages[e.id].disponible }))
            });
            const r = await stageAPI.proposerRepartition(departementId, anneeId, {
                tableau: cartes.map((c) => ({ cle: c.cle, tuteur_id: c.tuteur_courant || null })), formations: coches, portee
            });
            onProposition(r.data, s.data);
        } catch (e) { setErreur(message(e, "La proposition n'a pas pu être calculée")); }
        finally { setEnCours(false); }
    };

    return (
        <StageModale titre="Génération automatique" icone="fa-wand-magic-sparkles" classe="stg-modale-large" enCours={enCours}
                     libelleValider="Lancer la proposition" onValider={lancer} onFermer={onFermer}>
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
            <div className="stg-rep-gen-titre">1. Qui répartir ?</div>
            <div className="stg-rep-gen-forms">
                {formations.map((f) => (
                    <label key={f.libelle}>
                        <input type="checkbox" checked={coches.includes(f.libelle)} disabled={enCours}
                               onChange={(e) => setCoches((x) => (e.target.checked ? [...x, f.libelle] : x.filter((y) => y !== f.libelle)))} />
                        {' '}{f.libelle} <span className="stg-alt-sous">({sansTuteur(f.libelle)} sans tuteur)</span>
                    </label>
                ))}
            </div>
            <div className="stg-alt-sous" style={{ marginBottom: 6 }}>Pré-coché d'après le filtre « Formations » de la page.</div>
            <label className="stg-rep-radio"><input type="radio" name="repPortee" checked={portee === 'sans_tuteur'} disabled={enCours} onChange={() => setPortee('sans_tuteur')} />{' '}
                <b>Seulement les étudiants sans tuteur</b> <span className="stg-alt-sous">(recommandé : ne défait aucune affectation déjà annoncée)</span></label>
            <label className="stg-rep-radio" style={{ marginBottom: 12 }}><input type="radio" name="repPortee" checked={portee === 'tout'} disabled={enCours} onChange={() => setPortee('tout')} />{' '}
                Tout redistribuer <span className="stg-alt-sous">(sauf les visites déjà faites)</span></label>

            <div className="stg-rep-gen-titre">2. Combien de suivis par tuteur ?</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                Valeur par défaut :
                <input type="number" min={0} max={30} className="input-sm" style={{ width: 56 }} value={defaut} disabled={enCours} onChange={(e) => setDefaut(e.target.value)} aria-label="Valeur par défaut" />
                <button type="button" className="btn btn-outline btn-sm" disabled={enCours}
                        onClick={() => setReglages((r) => Object.fromEntries(Object.entries(r).map(([id, x]) => [id, { ...x, souhaite: defaut }])))}>
                    <i className="fa-solid fa-arrows-down-to-line" /> Appliquer à tous
                </button>
            </div>
            <table className="stg-rep-gen-table">
                <thead><tr><th>Tuteur</th><th>Disponible</th><th>Suivis souhaités</th><th>Déjà suivis</th></tr></thead>
                <tbody>
                    {tuteurs.map((e) => {
                        const r = reglages[e.id]; const deja = charge.get(e.id) || 0;
                        return (
                            <tr key={e.id} style={r.disponible ? undefined : { opacity: 0.55 }}>
                                <td><b>{nomEns(e)}</b> <span className="stg-alt-sous">{STATUTS[e.statut] || ''}</span>
                                    {e.souhait_origine === 'precedente' && <span className="stg-alt-sous" title="Repris de l'an dernier (même nom et prénom)"> · comme l'an dernier</span>}</td>
                                <td><input type="checkbox" checked={!!r.disponible} disabled={enCours} onChange={(ev) => poser(e.id, 'disponible', ev.target.checked)} aria-label={`${nomEns(e)} disponible`} /></td>
                                <td>{r.disponible
                                    ? <input type="number" min={0} max={30} className="input-sm" style={{ width: 56 }} value={r.souhaite} disabled={enCours} onChange={(ev) => poser(e.id, 'souhaite', ev.target.value)} aria-label={`Suivis souhaités de ${nomEns(e)}`} />
                                    : <span className="stg-alt-sous">—</span>}</td>
                                <td style={deja > Number(r.souhaite) ? { color: '#dc2626', fontWeight: 700 } : undefined}>{deja}</td>
                            </tr>
                        );
                    })}
                    {!tuteurs.length && <tr><td colSpan={4} className="stg-alt-sous">Aucun enseignant pour cette année : ajoutez-les dans le module Enseignants.</td></tr>}
                </tbody>
            </table>
            <div className="stg-alt-sous" style={{ marginTop: 8 }}>Ces nombres sont gardés pour l'année et servent aussi aux couleurs de charge du tableau de bord. Un enseignant non disponible n'est jamais proposé, mais reste choisissable à la main.</div>
            <div className="stg-rep-explication"><b>Comment c'est calculé :</b> un apprenti va d'abord à son tuteur de l'an dernier s'il est disponible et pas complet ; sinon chaque étudiant va au tuteur disponible le moins chargé par rapport à son nombre souhaité ; à égalité, celui qui suit déjà une entreprise de la <b>même ville</b>. Personne ne dépasse son nombre souhaité : ceux qui restent vont dans « Non affectés ».</div>
        </StageModale>
    );
}

/** Fenêtre « Enregistrer » : les changements, un commentaire facultatif. */
function FenetreEnregistrer({ changes, parId, enCours, onFermer, onEnregistrer }) {
    const [commentaire, setCommentaire] = useState('');
    const court = (id) => (id ? (parId.get(id)?.nom || '?') : 'sans tuteur');
    return (
        <StageModale titre="Enregistrer la répartition" icone="fa-floppy-disk" enCours={enCours} libelleValider="Enregistrer la version"
                     onValider={() => onEnregistrer(commentaire)} onFermer={onFermer}>
            <div style={{ marginBottom: 8 }}><b>{changes.length} changement{changes.length > 1 ? 's' : ''}</b> :{' '}
                {changes.slice(0, 20).map((c) => `${c.nom} ${c.prenom} → ${court(c.tuteur_courant)}`).join(' · ')}
                {changes.length > 20 && ` · … et ${changes.length - 20} autres`}
            </div>
            <label className="stg-alt-sous" htmlFor="repCommentaire">Commentaire <span>(facultatif — garde la raison)</span></label>
            <input id="repCommentaire" className="input-sm" style={{ width: '100%', boxSizing: 'border-box' }} maxLength={250} disabled={enCours}
                   placeholder="ex. Répartition des BUT3 après le jury de mars" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
        </StageModale>
    );
}

/** Fenêtre « Revenir à cette version » : ce qui change, contre l'état RÉEL, et les tuteurs déjà prévenus. */
function FenetreRevenir({ departementId, anneeId, version, brouillon, enCours, setEnCours, onFermer, onFait }) {
    const [ecart, setEcart] = useState(null);
    const [erreur, setErreur] = useState(null);
    useEffect(() => {
        let vivant = true;
        stageAPI.getEcartVersionRepartition(departementId, anneeId, version.id)
            .then((r) => vivant && setEcart(r.data)).catch((e) => vivant && setErreur(message(e, "L'écart n'a pas pu être calculé")));
        return () => { vivant = false; };
    }, [departementId, anneeId, version.id]);
    const revenir = async () => {
        setEnCours(true); setErreur(null);
        try { onFait((await stageAPI.revenirVersionRepartition(departementId, anneeId, version.id)).data); }
        catch (e) { setErreur(message(e, "Le retour n'a pas abouti")); }
        finally { setEnCours(false); }
    };
    return (
        <StageModale titre={`Revenir à la version du ${jourHeure(version.created_at)} ?`} icone="fa-rotate-left" enCours={enCours}
                     libelleValider="Revenir à cette version" onValider={ecart && ecart.changements.length ? revenir : undefined} onFermer={onFermer}>
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
            {!ecart && !erreur && <p>Calcul de l'écart…</p>}
            {ecart && (<>
                {ecart.changements.length ? (<>
                    <div style={{ marginBottom: 6 }}>Ce qui change :</div>
                    <div className="stg-rep-v-chg" style={{ marginBottom: 10 }}>
                        {ecart.changements.map((c) => <div key={c.cle}>{c.etudiant} : {c.avant || <i>sans tuteur</i>} → {c.apres || <i>sans tuteur</i>}</div>)}
                    </div>
                </>) : <p>La répartition actuelle est déjà celle de cette version : rien ne changerait.</p>}
                {ecart.tuteurs_prevenus.length > 0 && (
                    <div className="stg-rep-alerte"><i className="fa-solid fa-triangle-exclamation" />{' '}
                        <b>{ecart.tuteurs_prevenus.length} tuteur{ecart.tuteurs_prevenus.length > 1 ? 's ont' : ' a'} déjà été prévenu{ecart.tuteurs_prevenus.length > 1 ? 's' : ''}</b> pour ces étudiants
                        ({ecart.tuteurs_prevenus.map((t) => `${t.tuteur} le ${jour(t.le)}`).join(', ')}). Pensez à les informer du changement.
                    </div>
                )}
                {ecart.intouchables.length > 0 && <div className="stg-alt-sous" style={{ marginTop: 6 }}><i className="fa-solid fa-lock" /> Visite faite, non touché : {ecart.intouchables.map((x) => x.etudiant).join(', ')}.</div>}
                {ecart.non_remis.length > 0 && <div className="stg-alt-sous" style={{ marginTop: 6 }}>Tuteur non remis ({ecart.non_remis[0].raison}) : {ecart.non_remis.map((x) => `${x.etudiant} (${x.tuteur})`).join(', ')}.</div>}
                {ecart.plus_suivis.length > 0 && <div className="stg-alt-sous" style={{ marginTop: 6 }}>Plus suivis depuis : {ecart.plus_suivis.join(', ')}.</div>}
                {brouillon > 0 && <div className="stg-rep-alerte" style={{ marginTop: 8 }}><i className="fa-solid fa-triangle-exclamation" /> Vos {brouillon} placement{brouillon > 1 ? 's' : ''} non enregistré{brouillon > 1 ? 's' : ''} seront perdus.</div>}
                <div className="stg-alt-sous" style={{ marginTop: 8 }}>Les étudiants dont la visite est faite ne sont jamais touchés. Le retour en arrière crée lui-même une nouvelle version.</div>
            </>)}
        </StageModale>
    );
}

export default StageRepartition;
